// Bugs (platform admin) — the triage queue for problems reported via the tenant app's 🐞 button
// (shared/bugreport.js writes what this reads). List + an in-place detail panel (row click swaps
// the list for the detail view within this ONE screen; ‹ Back returns — no separate route/history
// entry, matching the "signal-swapped in the same screen" house pattern used nowhere else yet, so
// this file is its own reference). Read/triage tool for aboo platform staff only — no i18n here:
// the admin surface is English-only by design (see CLAUDE.md's role-split table; countries.js /
// migrations.js / user_detail.js are the precedent — none of them import shared/i18n).
import { html, signal } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { token } from '../shared/auth.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { sortTh, nextSort } from '../shared/sortcols.js';

const STATUS_TABS = [
  { key: '',            label: 'All' },
  { key: 'open',        label: 'Open' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'closed',      label: 'Closed' },
];
const PRIORITY_CLASS = { low: 'bug-pri-low', normal: 'bug-pri-normal', high: 'bug-pri-high', urgent: 'bug-pri-urgent' };
const STATUS_CLASS   = { open: 'bug-st-open', in_progress: 'bug-st-progress', closed: 'bug-st-closed' };
const STATUS_LABEL   = { open: 'Open', in_progress: 'In progress', closed: 'Closed' };
function statusLabel(s){ return STATUS_LABEL[s] || s || 'Open'; }

export function bugsScreen(){
  // ── list state ─────────────────────────────────────────────────────────────
  const rows = signal([]), total = signal(0), page = signal(1), pages = signal(1);
  const perPage = signal(DEFAULT_PER_PAGE), loading = signal(true), err = signal('');
  const status = signal(''), counts = signal({});
  const sort = signal({ key: 'created_at', dir: 'desc' });
  let timer, lastQ = '', loadSeq = 0;   // loadSeq: drop a stale response that resolves after a newer one

  // ── detail state (in-place — no route change) ─────────────────────────────
  const openId = signal(null), detail = signal(null), detailLoading = signal(false), detailErr = signal('');
  const commentBody = signal(''), commentSaving = signal(false), commentErr = signal('');
  const actionBusy = signal(false), actionErr = signal('');
  const zoomOpen = signal(false);

  async function load(){
    loading.value = true;
    const seq = ++loadSeq;
    const p = new URLSearchParams({ page: String(page.value), per_page: String(perPage.value),
      sort: sort.value.key, dir: sort.value.dir });
    if(status.value) p.set('status', status.value);
    if(lastQ.trim()) p.set('q', lastQ.trim());
    try {
      const r = await api.get('/admin/bugs?' + p.toString());
      if(seq !== loadSeq) return;                 // superseded by a newer load — discard
      rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
      counts.value = r.counts || {}; err.value = '';
    } catch(e){
      if(seq !== loadSeq) return;
      err.value = (e.data && e.data.error) || e.message || 'Could not load bugs'; rows.value = [];
    }
    if(seq === loadSeq) loading.value = false;
  }
  load();

  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }
  function setStatus(s){ if(status.value === s) return; status.value = s; page.value = 1; load(); }
  function onSort(key){
    sort.value = nextSort(sort.value, key, (key === 'priority' || key === 'status') ? 'asc' : 'desc');
    load();
  }

  // The screenshot endpoint needs the bearer header, which a bare <img src> can never send —
  // fetch the bytes with auth and hand the <img> a blob: URL instead (revoked on replace).
  const shotUrl = signal('');
  async function loadShot(id){
    if(shotUrl.value){ try { URL.revokeObjectURL(shotUrl.value); } catch {} }
    shotUrl.value = '';
    try {
      const r = await fetch('/api/admin/bugs/' + id + '/screenshot',
                            { headers: { Authorization: 'Bearer ' + token() } });
      if(r.ok) shotUrl.value = URL.createObjectURL(await r.blob());
    } catch { /* no screenshot — the section stays hidden */ }
  }

  async function openDetail(id){
    openId.value = id; detail.value = null; detailErr.value = ''; detailLoading.value = true;
    commentBody.value = ''; commentErr.value = ''; actionErr.value = ''; zoomOpen.value = false;
    try {
      detail.value = await api.get('/admin/bugs/' + id);
      if(detail.value && detail.value.has_screenshot) loadShot(id);
    }
    catch(e){ detailErr.value = (e.data && e.data.error) || e.message || 'Could not load this bug'; }
    detailLoading.value = false;
  }
  function backToList(){ openId.value = null; detail.value = null; zoomOpen.value = false; }

  async function toggleStatus(){
    if(!detail.value) return;
    const id = detail.value.id;
    const closing = detail.value.status !== 'closed';
    actionBusy.value = true; actionErr.value = '';
    try {
      await api.post('/admin/bugs/' + id + '/' + (closing ? 'close' : 'reopen'), {});
      detail.value = await api.get('/admin/bugs/' + id);
      await load();                                // list counts/pill must reflect the change on Back
    } catch(e){ actionErr.value = (e.data && e.data.error) || e.message || 'Action failed'; }
    actionBusy.value = false;
  }

  async function addComment(e){
    e.preventDefault();
    if(!commentBody.value.trim()){ commentErr.value = 'Enter a comment.'; return; }
    commentSaving.value = true; commentErr.value = '';
    try {
      await api.post('/admin/bugs/' + detail.value.id + '/comments', { body: commentBody.value.trim() });
      commentBody.value = '';
      detail.value = await api.get('/admin/bugs/' + detail.value.id);
    } catch(e){ commentErr.value = (e.data && e.data.error) || e.message || 'Could not add comment'; }
    commentSaving.value = false;
  }

  // Esc closes the screenshot lightbox — ONE window listener, replaced (never stacked) on every
  // mount, matching the house idiom for imperative global listeners (see tenant/journals.js's
  // onRegScroll / window.__jrnRegScroll).
  function onKey(e){ if(e.key === 'Escape' && zoomOpen.value) zoomOpen.value = false; }
  if(window.__bugZoomKey) window.removeEventListener('keydown', window.__bugZoomKey);
  window.__bugZoomKey = onKey;
  window.addEventListener('keydown', onKey);

  // ── list ───────────────────────────────────────────────────────────────────
  // chips / listBody / detailBody are PLAIN functions, always invoked directly (chips(), never
  // ${chips}) from inside the ONE reactive `body` slot below — a bare function reference here would
  // be a reactive slot NESTED inside another (body already is one), which is exactly the tina4
  // append-on-re-render trap from CLAUDE.md. Signals each reads become body's own dependencies,
  // same as list()/pg() in countries.js or balanceCard()/contactCard() in document_detail.js.
  const chips = () => {
    const c = counts.value, cur = status.value;
    return html`<div class="doc-tabs bug-tabs">${STATUS_TABS.map(s => html`
      <button type="button" class=${'doc-tab' + (cur === s.key ? ' active' : '')} @click=${() => setStatus(s.key)}>
        ${s.label}<span class="doc-tab-n">${s.key ? (c[s.key] || 0) : ((c.open || 0) + (c.in_progress || 0) + (c.closed || 0))}</span>
      </button>`)}</div>`;
  };

  const listBody = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    if(!rows.value.length) return html`<div class="muted-block">No bugs match.</div>`;
    const cur = sort.value;
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    return html`<div class="list-wrap">${pg()}
      <div class="table-wrap"><table class="cb-table bug-table">
        <thead><tr>
          <th>Title</th><th>Company</th>
          ${sortTh('Priority', 'priority', cur, onSort)}
          ${sortTh('Status', 'status', cur, onSort)}
          ${sortTh('Created', 'created_at', cur, onSort)}
          <th></th>
        </tr></thead>
        <tbody>${rows.value.map(b => html`<tr class="bug-row" @click=${() => openDetail(b.id)}>
          <td>${b.title}${b.has_screenshot ? html`<span class="bug-cam" title="Has screenshot">📷</span>` : ''}
            <div class="row-sub">${(b.reporter && b.reporter.email) || '—'}</div></td>
          <td>${(b.company && b.company.name) || '—'}</td>
          <td><span class=${'bug-pill ' + (PRIORITY_CLASS[b.priority] || 'bug-pri-normal')}>${b.priority}</span></td>
          <td><span class=${'bug-pill ' + (STATUS_CLASS[b.status] || 'bug-st-open')}>${statusLabel(b.status)}</span></td>
          <td class="mono">${b.created_at || '—'}</td>
          <td class="row-chevron">${icon('chevron-right', {size: 14})}</td>
        </tr>`)}</tbody>
      </table></div>
      ${pg()}
    </div>`;
  };

  // ── detail (in place) ─────────────────────────────────────────────────────
  const detailBody = () => {
    if(detailLoading.value) return html`<div class="page bug-detail"><div class="muted-block">Loading…</div></div>`;
    if(detailErr.value) return html`<div class="page bug-detail">
      <a class="back-link" @click=${backToList}>${icon('chevron-left', {size: 14})} Bugs</a>
      <div class="muted-block">${detailErr.value}</div></div>`;
    const d = detail.value;
    if(!d) return html`<div class="page bug-detail"><div class="muted-block">Loading…</div></div>`;
    const m = d.meta || {};
    const zoom = zoomOpen.value, cErr = commentErr.value, cSaving = commentSaving.value;
    const aBusy = actionBusy.value, aErr = actionErr.value;
    return html`<div class="page bug-detail">
      <div class="page-head">
        <a class="back-link" @click=${backToList}>${icon('chevron-left', {size: 14})} Bugs</a>
        <h1 class="page-title">${d.title}</h1>
        <div class="page-sub">
          <span class=${'bug-pill ' + (PRIORITY_CLASS[d.priority] || 'bug-pri-normal')}>${d.priority}</span>
          <span class=${'bug-pill ' + (STATUS_CLASS[d.status] || 'bug-st-open')}>${statusLabel(d.status)}</span>
          · ${(d.reporter && (d.reporter.display_name || d.reporter.email)) || 'Unknown reporter'}
          · ${(d.company && d.company.name) || '—'}
          · ${d.created_at || ''}
        </div>
      </div>

      ${d.description ? html`<div class="field mb-2"><div class="field-label">Description</div><div class="field-val bug-desc">${d.description}</div></div>` : ''}
      ${d.url ? html`<div class="muted-sub mb-2">Page: <span class="mono">${d.url}</span></div>` : ''}

      ${d.has_screenshot && shotUrl.value ? html`<div class="bug-shot-wrap">
        <img class="bug-shot-thumb" src=${shotUrl.value} alt="Screenshot"
          title="Click to zoom" @click=${() => { zoomOpen.value = true; }}>
      </div>` : ''}

      <div class="rail-card-title mt-2">Environment</div>
      <div class="detail-grid mb-2">
        <div class="field"><div class="field-label">Browser</div><div class="field-val">${m.browser || '—'}</div></div>
        <div class="field"><div class="field-label">OS</div><div class="field-val">${m.os || '—'}</div></div>
        <div class="field"><div class="field-label">Viewport</div><div class="field-val">${m.viewport || '—'}</div></div>
        <div class="field"><div class="field-label">App version</div><div class="field-val">${m.app_version || '—'}</div></div>
      </div>

      ${(m.console && m.console.length) ? html`<div class="bug-console mb-2">
        <div class="rail-card-title">Console (last ${m.console.length})</div>
        <pre class="bug-console-pre mono">${m.console.join('\n')}</pre>
      </div>` : ''}

      <div class="rail-card-title">Comments</div>
      <div class="bug-comments mb-2">
        ${(d.comments || []).length
          ? (d.comments || []).map(c => html`<div class="bug-comment">
              <div class="bug-comment-head"><b>${(c.author && (c.author.display_name || c.author.email)) || 'Unknown'}</b>
                <span class="row-sub">${c.created_at || ''}</span></div>
              <div class="bug-comment-body">${c.body}</div>
            </div>`)
          : html`<div class="muted-sub">No comments yet.</div>`}
      </div>
      <form class="bug-comment-form" @submit=${addComment}>
        <textarea class="form-control mb-2" rows="2" .value=${commentBody}
          @input=${e => { commentBody.value = e.target.value; if(cErr) commentErr.value = ''; }}
          placeholder="e.g. Fixed in build 0.1.2.0"></textarea>
        ${cErr ? html`<div class="alert alert-danger py-2 mb-2">${cErr}</div>` : ''}
        <button type="submit" class="btn btn-outline" ?disabled=${cSaving}>${cSaving ? 'Adding…' : '+ Add comment'}</button>
      </form>

      ${aErr ? html`<div class="alert alert-danger py-2 mb-2 mt-2">${aErr}</div>` : ''}
      <div class="form-actions mt-2">
        <span class="form-actions-spacer"></span>
        <button type="button" class=${'btn ' + (d.status === 'closed' ? 'btn-outline' : 'btn-danger')}
          ?disabled=${aBusy} @click=${toggleStatus}>
          ${aBusy ? 'Working…' : (d.status === 'closed' ? 'Reopen' : 'Close bug')}
        </button>
      </div>

      ${zoom && shotUrl.value ? html`<div class="bug-zoom" @click=${() => { zoomOpen.value = false; }}>
        <img class="bug-zoom-img" src=${shotUrl.value} alt="Screenshot (zoomed)">
      </div>` : ''}
    </div>`;
  };

  // ── screen: list ↔ detail, swapped in place (no route change) ──────────────
  const body = () => {
    const id = openId.value;   // subscribe: this is what drives the swap
    if(id != null) return detailBody();
    return html`<div class="page">
      <div class="page-head">
        <div><h1 class="page-title">Bugs</h1>
          <div class="page-sub">User-reported problems from the 🐞 button in the app</div></div>
      </div>
      <div class="toolbar">
        <input class="form-control" type="search" placeholder="Search… e.g. invoice totals or jane@" @input=${onSearch}>
        ${perPageSelect(perPage.value, onPer)}
      </div>
      ${chips()}
      ${listBody()}
    </div>`;
  };

  return shell('bugs', 'Bugs', body);
}
